/**
 * CapacityWeekExpand.tsx
 * Inline day-by-day breakdown revealed when a week tile in the "Team Allocation
 * & Weekly Capacity" grid is clicked (staffing-allocation tile restyle
 * 2026-07-13). Mirrors the Employee Schedule matrix's expand-row idiom
 * (WeekDetailBreakdown): one labelled row per assignment with its bar spanning
 * the Sat→Fri days it occupies — showing WHICH work order / quote (+ task) takes
 * WHICH days — instead of the retired per-day "N tests" count cells.
 *
 * Read-only: no drag-to-extend here (that lives in the Employee Schedule's own
 * breakdown). Data comes from GET /schedule/employee-week-detail
 * (fetchEmployeeWeekDetail) — the SAME payload the EmployeeWeekCellPopup used:
 * the assignment bars + the per-source allocation breakdown (D6).
 *
 * Colors via theme.schedule.* tokens — no hex literals (CODING-STANDARDS).
 */

import React from 'react';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import type { Theme } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  dayIndexInWeek,
  datedDayLabels,
  weekOfMondayLabel,
  workingDaysCoveredInWeek,
  workingDaysInWeek,
  assignmentPrimaryLabel,
} from '../../../../components/schedule/employeeScheduleShared';
import {
  allocationBand,
  type AllocationBand,
} from '../../../../utils/scheduleAllocationBands';
import {
  formatCrossScenarioSource,
  type AssignmentBarDetail,
  type CrossScenarioSource,
  type SourceAllocationDetail,
} from '../../../../services/scheduleEmployeeSchedule';
// Reuse the popup's unit-tested per-source ordering (biggest share first) so the
// inline breakdown and the (retained) popup can't drift — and no duplicate sort.
import { orderSourceAllocations } from '../EmployeeWeekCellPopup';

const META_W = 240;
// Right-hand column reports each row's DAY COVERAGE (how many working days of the
// week that WO/quote occupies) — NOT an allocation %. Labelled "Days" so it stops
// competing with the real count-once allocation % (the "Allocation by source"
// summary below). BUG-2026-0141 #4.
const PCT_W = 64;

export interface CapacityWeekExpandProps {
  weekStart: string;
  assignments: AssignmentBarDetail[];
  sourceAllocations: SourceAllocationDetail[];
  /** OTHER draft plans holding this person that week (advisory soft note). */
  crossSources?: CrossScenarioSource[];
  isLoading: boolean;
  error: string | null;
  holidaySet: ReadonlySet<string>;
}

/** Bar colour for an assignment's source type (task vs each absence kind). */
function barColorForSource(
  theme: Theme,
  sourceType: AssignmentBarDetail['sourceType'],
): string {
  const s = theme.schedule;
  switch (sourceType) {
    case 'Vacation': return s.absenceVacation;
    case 'Sick': return s.absenceSick;
    case 'FieldWork': return s.absenceFieldWork;
    case 'Other': return s.absenceOther;
    default: return s.taskAssignment;
  }
}

/** Utilization-band colour for a per-source allocation %. */
function bandColor(theme: Theme, percent: number): string {
  const band: AllocationBand = allocationBand(percent);
  return band === 'over' ? theme.schedule.allocOver
    : band === 'at' ? theme.schedule.allocAt
      : band === 'healthy' ? theme.schedule.allocHealthy
        : theme.schedule.allocUnder;
}

export const CapacityWeekExpand: React.FC<CapacityWeekExpandProps> = ({
  weekStart,
  assignments,
  sourceAllocations,
  crossSources,
  isLoading,
  error,
  holidaySet,
}) => {
  const theme = useTheme();
  const labels = datedDayLabels(weekStart);
  const weekWorkingDays = workingDaysInWeek(weekStart, holidaySet);
  const sorted = [...assignments].sort(
    (a, b) => a.startDate.localeCompare(b.startDate) || a.name.localeCompare(b.name),
  );
  const ordered = orderSourceAllocations(sourceAllocations);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.border.color}`,
        borderRadius: `${theme.border.radius}px`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        my: 0.5,
      }}
    >
      {/* Header row — Week label + Sat→Fri dated day headers + a Week% column. */}
      <Box sx={{ display: 'flex', borderBottom: `1px solid ${theme.border.color}`, backgroundColor: theme.schedule.selectionBg }}>
        <Box sx={{ width: META_W, flexShrink: 0, px: 1.5, py: 0.6, fontSize: '0.7rem', fontWeight: 700 }}>
          Week of {weekOfMondayLabel(weekStart)}
        </Box>
        {labels.map((lbl, i) => (
          <Box
            key={lbl}
            sx={{
              flex: 1,
              textAlign: 'center',
              py: 0.6,
              fontSize: '0.62rem',
              fontWeight: 600,
              color: i < 2 ? 'text.disabled' : 'text.secondary',
              borderLeft: `1px solid ${theme.border.color}`,
              backgroundColor: i < 2 ? alpha(theme.palette.text.disabled, 0.05) : 'transparent',
            }}
          >
            {lbl}
          </Box>
        ))}
        <Box sx={{ width: PCT_W, flexShrink: 0, textAlign: 'center', py: 0.6, fontSize: '0.62rem', fontWeight: 700, borderLeft: `1px solid ${theme.border.color}` }}>
          Days
        </Box>
      </Box>

      {/* Body */}
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.25 }}>
          <CircularProgress size={13} thickness={5} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            Loading this week&rsquo;s work&hellip;
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="warning" sx={{ m: 1, py: 0.25, fontSize: '0.72rem' }}>{error}</Alert>
      ) : sorted.length === 0 ? (
        <Box sx={{ px: 1.5, py: 1.1, fontSize: '0.72rem', color: 'text.secondary' }}>
          No work scheduled this week.
        </Box>
      ) : (
        sorted.map((a) => {
          const color = barColorForSource(theme, a.sourceType);
          const s = Math.max(0, Math.min(6, dayIndexInWeek(a.startDate, weekStart)));
          const e = Math.max(0, Math.min(6, dayIndexInWeek(a.endDate, weekStart)));
          const leftPct = (s / 7) * 100;
          const widthPct = ((Math.max(s, e) - s + 1) / 7) * 100;
          const days = workingDaysCoveredInWeek(a.startDate, a.endDate, weekStart, holidaySet);
          const isAbsence = a.sourceType !== 'Task';
          return (
            <Box
              key={a.assignmentKey}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                minHeight: 30,
                borderBottom: `1px solid ${theme.border.color}`,
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              {/* meta — swatch + WO/quote descriptive label + status pill + dates */}
              <Box sx={{ width: META_W, flexShrink: 0, px: 1.5, py: 0.4, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: color, flexShrink: 0 }} />
                  <Typography
                    title={`${assignmentPrimaryLabel(a)} · ${a.name}`}
                    sx={{ fontSize: '0.72rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {assignmentPrimaryLabel(a)}
                  </Typography>
                  {!isAbsence && (
                    <Box
                      component="span"
                      title={a.status === 'Tentative' ? 'Tentative — proposed in this plan' : 'Booked — on the live schedule'}
                      sx={{
                        flexShrink: 0,
                        px: 0.4,
                        borderRadius: '3px',
                        fontSize: '0.5rem',
                        fontWeight: 800,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        color: a.status === 'Tentative' ? theme.schedule.machineTime : theme.palette.success.main,
                        backgroundColor: alpha(a.status === 'Tentative' ? theme.schedule.machineTime : theme.palette.success.main, 0.14),
                      }}
                    >
                      {a.status === 'Tentative' ? 'Tentative' : 'Booked'}
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', ml: 1.6 }}>
                  {a.startDate} &ndash; {a.endDate}
                </Typography>
              </Box>

              {/* day area — 7 gridded columns with the bar spanning its days */}
              <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                {labels.map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      borderLeft: `1px solid ${theme.border.color}`,
                      backgroundColor: i < 2 ? alpha(theme.palette.text.disabled, 0.05) : 'transparent',
                    }}
                  />
                ))}
                <Box
                  title={`${assignmentPrimaryLabel(a)} · ${a.startDate} – ${a.endDate}`}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    height: 14,
                    borderRadius: '4px',
                    backgroundColor: color,
                    opacity: a.status === 'Tentative' ? 0.72 : 1,
                  }}
                />
              </Box>

              {/* week % for the row */}
              <Box
                title={`${isAbsence ? 'day off' : 'busy'} ${days} of ${weekWorkingDays} working day${weekWorkingDays !== 1 ? 's' : ''}`}
                sx={{ width: PCT_W, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderLeft: `1px solid ${theme.border.color}` }}
              >
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.1 }}>{days}</Typography>
                <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', lineHeight: 1.1 }}>
                  {days === 1 ? 'day' : 'days'}{isAbsence ? ' off' : ''}
                </Typography>
              </Box>
            </Box>
          );
        })
      )}

      {/* Per-source allocation summary (D6) — which WO/quote takes what share. */}
      {!isLoading && !error && ordered.length > 0 && (
        <Box sx={{ px: 1.5, py: 0.6, borderTop: `1px solid ${theme.border.color}`, backgroundColor: theme.table.stripeBg }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.disabled', display: 'block', mb: 0.25 }}>
            Allocation by source
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {ordered.map((src) => (
              <Box key={`${src.sourceType}:${src.sourceKey ?? 'manual'}`} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600 }}>{src.label}</Typography>
                {src.name && (
                  <Typography color="text.secondary" title={src.name} sx={{ fontSize: '0.62rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {src.name}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: bandColor(theme, src.percent) }}>
                  {Math.round(src.percent)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Advisory: this person is tentatively held in another draft plan. */}
      {!isLoading && !error && crossSources && crossSources.length > 0 && (
        <Box sx={{ px: 1.5, py: 0.5, borderTop: `1px dashed ${alpha(theme.schedule.conflictSoft, 0.5)}`, backgroundColor: alpha(theme.schedule.conflictSoft, 0.05), display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.6 }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.62rem', color: theme.schedule.conflictSoft }}>
            Also held in another plan (advisory):
          </Typography>
          {crossSources.map((cs) => (
            <Chip
              key={cs.scenarioKey}
              size="small"
              label={formatCrossScenarioSource(cs)}
              sx={{ height: 18, fontSize: '0.58rem', backgroundColor: alpha(theme.schedule.conflictSoft, 0.12), color: 'text.secondary', border: `1px solid ${alpha(theme.schedule.conflictSoft, 0.5)}`, '& .MuiChip-label': { px: 0.5 } }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
